import { FC, Dispatch, SetStateAction } from 'react';
import { Button, Popconfirm, Space, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import DownloadFileButton from 'components/DownloadFileButton';
import ENVIRONMENT from 'config/environment';
import { defaultStyles, FileIcon } from 'react-file-icon';
import { UserResponse } from 'types';
import { UploadFileApi, UploadResponse } from 'types/upload-type';
import ButtonDownloadAllFiles from '../ButtonDownloadAllFiles';
import styles from './styles.module.less';

interface CommentUploadsProps {
  comment: any;
  user: UserResponse;
  editing: boolean;
  images: UploadFileApi[];
  setImages: Dispatch<SetStateAction<UploadFileApi[]>>;
  onImgDelete: (uploadUuid: string, commentUuid: string) => void;
  setFileViewerIndex?: any;
  setFileViewerOpen?: any;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov'];
const MAX_NAME_LENGTH = 35;

const truncateName = (name: string, extension: string): string => {
  const nameWithoutExt = name.endsWith(`.${extension}`)
    ? name.slice(0, -`.${extension}`.length)
    : name;
  return nameWithoutExt.length > MAX_NAME_LENGTH
    ? `${nameWithoutExt.slice(0, MAX_NAME_LENGTH)}...`
    : nameWithoutExt;
};

const FileThumbnail: FC<{ ext: string; url: string; name: string; posterUrl?: string }> = ({ ext, url, name, posterUrl }) => {
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={name} />;
  }

  if (VIDEO_EXTENSIONS.includes(ext)) {
    return (
      <video
        src={`${url}#t=0.1`}
        poster={posterUrl}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        muted
        playsInline
        preload="metadata"
      />
    );
  }

  const customStyles = ext in defaultStyles ? defaultStyles[ext as keyof typeof defaultStyles] : {};
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 10 }}>
      <FileIcon extension={ext.toUpperCase()} {...customStyles} />
    </div>
  );
};

const CommentUploads: FC<CommentUploadsProps> = ({
  comment,
  user,
  editing,
  images,
  setImages,
  onImgDelete,
  setFileViewerIndex,
  setFileViewerOpen
}) => {
  if (comment.uploads.length === 0 && !(editing && images.length > 0)) {
    return null;
  }

  return (
    <Space size={10} direction="vertical" style={{ width: '100%', marginTop: 12 }}>
      {comment.uploads.length > 1 && (
        <div style={{ textAlign: 'right' }}>
          <ButtonDownloadAllFiles
            resource="file"
            ids={comment.uploads.map((upload: UploadResponse) => upload.uuid)}
            style={{ margin: 0, width: 'auto', fontSize: 12, padding: '0.15rem 0.6rem' }}
            label="Download all"
            showZippingLabel
          />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {comment.uploads.map((upload: UploadResponse, uploadIdx: number) => {
          const ext = upload.extension.toLowerCase();
          const url = `${ENVIRONMENT.UPLOADS_URL}/${upload.uuid}.${upload.extension}`;
          const truncatedName = truncateName(upload.name, upload.extension);

          return (
            <Tooltip title={truncatedName} key={upload.uuid}>
              <div
                className={styles.uploadItem}
                style={{ position: 'relative', width: 60, height: 60, borderRadius: 4, overflow: 'hidden', cursor: 'pointer' }}
              >
                <FileThumbnail
                  ext={ext}
                  url={url}
                  name={upload.name}
                  posterUrl={`${ENVIRONMENT.UPLOADS_URL}/${upload.uuid}.jpg`}
                />

                <div className={styles.uploadOverlay}>
                  <Button
                    type="text"
                    icon={<EyeOutlined style={{ color: 'white' }} />}
                    style={{ width: '26px', height: '26px' }}
                    onClick={() => { setFileViewerIndex(uploadIdx); setFileViewerOpen(true); }}
                  />
                  <DownloadFileButton
                    uuid={upload.uuid}
                    name={upload.name}
                    style={{ width: '26px', height: '26px' }}
                    icon={<DownloadOutlined style={{ color: 'white' }} />}
                    hideTooltip
                  />
                  {!comment.deleted_at && comment.creator.uuid === user.uuid && (
                    <Popconfirm
                      placement="bottom"
                      title="Do you want to delete this file?"
                      onConfirm={() => onImgDelete(upload.uuid, comment.uuid)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        style={{ width: '26px', height: '26px' }}
                        type="text"
                        icon={<DeleteOutlined style={{ color: 'white' }} />}
                      />
                    </Popconfirm>
                  )}
                </div>
              </div>
            </Tooltip>
          );
        })}

        {editing && images.map((img) => {
          const ext = (img.response?.extension ?? '').toLowerCase();
          const url = img.response?.url ?? `${ENVIRONMENT.UPLOADS_URL}/${img.response?.uuid}.${ext}`;
          const truncatedName = truncateName(img.name, ext);

          return (
            <Tooltip title={truncatedName} key={img.response?.uuid ?? img.uid}>
              <div
                className={styles.uploadItem}
                style={{ position: 'relative', width: 60, height: 60, borderRadius: 4, overflow: 'hidden', cursor: 'pointer' }}
              >
                <FileThumbnail ext={ext} url={url} name={img.name} />
                <div className={styles.uploadOverlay}>
                  <Button
                    type="text"
                    style={{ width: '26px', height: '26px' }}
                    icon={<DeleteOutlined style={{ color: 'white' }} />}
                    onClick={() => setImages((prev) => prev.filter((el) => el.response?.uuid !== img.response?.uuid))}
                  />
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </Space>
  );
};

export default CommentUploads;